import { FC, useState, useEffect, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { DiscountStatistics } from 'pages/Discounts/DiscountStatistics';
import { DiscountCodeZ as DiscountCode } from '@libs/schemas';
import { EditDiscountCodeSheet } from 'pages/Discounts/EditDiscountCodeSheet';
import { trpc } from 'src/trpc';
import { Loader } from 'components/Loader';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { createDiscountCodeColumns } from './DiscountCodeColumns';
import { Search } from 'src/components/Search';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'src/components/Debounce';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from 'src/context/OrganizationContext';

export const DiscountsDashboard: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<DiscountCode | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '25',
    search: '',
    sortDirection: 'desc',
    sortKey: 'createdAt',
    archived: 'active',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '25');
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, isPending, isFetching } =
    trpc.admin.discountCodes.getAllDiscountCodes.useQuery(
      {
        offset,
        limit,
        search: params.get('search') ?? '',
        sortDirection: (params.get('sortDirection') ?? 'desc') as 'asc' | 'desc',
        sortKey: params.get('sortKey')?.trim() || 'createdAt',
        archived: (params.get('archived') ?? 'active') as 'active' | 'archived' | 'all',
      },
      {
        placeholderData: keepPreviousData,
        enabled: isResolved && !!organizationId,
      },
    );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  useEffect(() => {
    if (sorting.length) {
      const newParams = new URLSearchParams(params);
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id || 'createdAt');
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [sorting, params, setParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleStatusFilter = (value: string) => {
    const newParams = new URLSearchParams(params);
    newParams.set('archived', value);
    newParams.set('offset', '0'); // Reset to first page
    setParams(newParams);
  };

  const handleEditDiscountCode = (discountCode: DiscountCode) => {
    setSelectedDiscountCode(discountCode);
    setIsOpen(true);
  };

  const discountCodes = (data?.discountCodes || []) as unknown as DiscountCode[];
  const total = data?.total || 0;

  const columns = createDiscountCodeColumns(handleEditDiscountCode);

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching && !data)) {
    return <Loader title="Loading discount codes" />;
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <DiscountStatistics discountCodes={discountCodes} total={total} />

      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search discount codes by code"
            isLoading={isLoading}
          />
        </div>
        <div className="w-48">
          <Select value={params.get('archived') ?? 'active'} onValueChange={handleStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Codes</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="archived">Inactive Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data Table */}
      <div className="flex flex-col h-full">
        <DataTable
          columns={columns}
          data={discountCodes}
          currentPage={currentPage}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={total > offset + limit}
          hasPreviousPage={offset > 0}
          onPageChange={(change) => {
            const newOffset = Math.max(0, offset + change * limit);
            setParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set('offset', newOffset.toString());
              return newParams;
            });
          }}
          pageCount={Math.ceil(total / limit)}
          total={total}
          resourceName="discount"
          offset={offset}
          limit={limit}
          onRowClick={handleEditDiscountCode}
        />
      </div>

      {/* Edit Modal */}
      <EditDiscountCodeSheet
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedDiscountCode(null);
        }}
        selectedDiscountCode={selectedDiscountCode}
      />
    </div>
  );
};
